import React, { useState } from 'react';
import type { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import ExportService from '../../../../../services/export.service';

type ExportKey = 'reservations' | 'transactions' | 'invoices' | 'invoiceLineItems';

const T = 'layout.pageTexts.dashboard.pageTexts.dataExports';

const EXPORTS: { key: ExportKey; icon: string; variant: 'primary' | 'success' | 'warning' | 'info' }[] = [
  { key: 'reservations', icon: 'ri-calendar-check-line', variant: 'primary' },
  { key: 'transactions', icon: 'ri-exchange-dollar-line', variant: 'success' },
  { key: 'invoices', icon: 'ri-file-list-3-line', variant: 'warning' },
  { key: 'invoiceLineItems', icon: 'ri-list-check-2', variant: 'info' },
];

const getFileName = (disposition: string | undefined, fallback: string): string => {
  const match = disposition?.match(/filename="?([^";]+)"?/i);
  return match ? match[1] : fallback;
};

const saveBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
};

const DataExports: React.FC = () => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<ExportKey | null>(null);

  const handleDownload = async (key: ExportKey) => {
    setBusy(key);
    try {
      const response: AxiosResponse<Blob> = await ExportService[key]();
      saveBlob(response.data, getFileName(response.headers?.['content-disposition'], `${key}.xlsx`));
    } catch {
      // the HTTP interceptor already reports the error
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="row mb-4">
        <div className="col-12">
          <h5 className="m-0 fw-bold">{t(`${T}.title`)}</h5>
          <small className="text-muted">{t(`${T}.subTitle`)}</small>
        </div>
      </div>
      <div className="row g-6 mb-6">
        {EXPORTS.map(({ key, icon, variant }) => (
          <div key={key} className="col-sm-6 col-xl-3">
            <button
              type="button"
              className="card w-100 text-start cursor-pointer"
              style={{ opacity: busy !== null && busy !== key ? 0.6 : 1 }}
              disabled={busy !== null}
              onClick={() => handleDownload(key)}
            >
              <div className="card-body w-100">
                <div className="d-flex align-items-center">
                  <div className="avatar me-4">
                    <div className={`avatar-initial bg-label-${variant} rounded-3`}>
                      <i className={`${icon} icon-24px`}></i>
                    </div>
                  </div>
                  <div className="card-info flex-grow-1">
                    <h6 className="mb-0">{busy === key ? t(`${T}.downloading`) : t(`${T}.${key}`)}</h6>
                    <small className="text-muted">.xlsx</small>
                  </div>
                  {busy === key ? (
                    <span className={`spinner-border spinner-border-sm text-${variant}`} role="status" aria-hidden="true"></span>
                  ) : (
                    <i className="ri-download-2-line icon-24px text-muted"></i>
                  )}
                </div>
              </div>
            </button>
          </div>
        ))}
      </div>
    </>
  );
};

export default DataExports;
